import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getChats, createChat } from '../apis/chatApi';
import { CreateChatRequest } from '../apis/types';

export const useChats = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching chats
  const {
    data: chats = [],
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: ['chats'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getChats(token);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Mutation for creating new chats
  const createChatMutation = useMutation({
    mutationFn: async (data: CreateChatRequest) => {
      const token = await getAccessTokenSilently();
      return createChat(data, token);
    },
    onSuccess: () => {
      // Invalidate and refetch chats
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
    onError: error => {
      console.error('Error creating chat:', error);
    },
  });

  const error =
    queryError?.message || createChatMutation.error?.message || null;

  return {
    chats,
    loading,
    creating: createChatMutation.isPending,
    error,
    createNewChat: createChatMutation.mutateAsync,
    refreshChats: () => {
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  };
};

